import React from "react"
import { Collapse, Flex, Input, Radio, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text, Title } from "ui/Typography"
import { numberToCurrency } from "helpers/string"
import { BACKEND_CARD_FEE_TITLE, getSavedBookingTotals } from "utils/bookingCalculations"
import styles from "./index.module.css"

const PaymentBreakdown = ({ breakdownData, breakdownLoading, refundType, partialAmount, partialAmountError, isRefundDisabled, handleRadioChange, handlePartialAmountChange }) => {
  const { t } = useTranslation()

  if (breakdownLoading) {
    return (
      <Flex justify="center" align="center" className={styles.breakdownLoader}>
        <Spin indicator={<LoadingOutlined spin />} />
      </Flex>
    )
  }
  if (!breakdownData) {
    return <Text className={styles.errorText}>{t("bookings.cancel_modal.breakdown_load_error")}</Text>
  }

  // The payment processing fee is a transaction fee on the amount charged, not part of the booking
  // price, so it is kept out of Taxes & Fees and shown on its own line under the total — the same
  // rule every other screen follows. BACKEND_CARD_FEE_TITLE is the payload's title, not a label.
  const customFees = breakdownData.custom_fees || []
  const { processingFee, bookingTotal, totalCharged } = getSavedBookingTotals(breakdownData)
  const taxAndFeeItems = customFees.filter((fee) => fee.title !== BACKEND_CARD_FEE_TITLE)

  const totalTaxes =
    (breakdownData.tax_and_fee || 0) + taxAndFeeItems.reduce((sum, fee) => sum + (fee.price || 0), 0)

  const taxBreakdownItems = [
    ...(breakdownData.tax_and_fee > 0 ? [{ id: "tax", title: t("bookings.cancel_modal.taxes_and_fees"), price: breakdownData.tax_and_fee }] : []),
    ...taxAndFeeItems
  ]
  const hasTaxBreakdown = taxBreakdownItems.length > 0

  return (
    <>
      <Title level={5} className={styles.sectionTitle}>{t("bookings.cancel_modal.breakdown_title")}</Title>
      <Flex vertical gap={12} className={styles.breakdownSection}>
        <Flex justify="space-between" align="center" className={styles.breakdownRow}>
          <Text>{t("bookings.cancel_modal.total_booking_amount")}</Text>
          <Text strong>{numberToCurrency(bookingTotal)}</Text>
        </Flex>
        {hasTaxBreakdown ? (
          <Collapse
            ghost
            items={[
              {
                key: "tax-fees",
                styles: {
                  header: { padding: 0 },
                  body: { padding: "4px 0 0 16px" }
                },
                label: (
                  <Flex justify="space-between" align="center" className={styles.breakdownRow}>
                    <Text>{t("bookings.cancel_modal.taxes_and_fees")}</Text>
                    <Text strong>{numberToCurrency(totalTaxes)}</Text>
                  </Flex>
                ),
                children: taxBreakdownItems.map((fee) => (
                  <Flex key={fee.id} justify="space-between" align="center" style={{ marginTop: 4 }}>
                    <Text type="secondary">{fee.title}</Text>
                    <Text type="secondary">{numberToCurrency(fee.price)}</Text>
                  </Flex>
                ))
              }
            ]}
          />
        ) : (
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Text>{t("bookings.cancel_modal.taxes_and_fees")}</Text>
            <Text strong>{numberToCurrency(totalTaxes)}</Text>
          </Flex>
        )}
        {processingFee > 0 && (
          <>
            <Flex justify="space-between" align="center" className={styles.breakdownRow}>
              <Text>{t("booking.labels.card_processing_fee")}</Text>
              <Text strong>+{numberToCurrency(processingFee)}</Text>
            </Flex>
            <Flex justify="space-between" align="center" className={styles.breakdownRow}>
              <Text>{t("booking.labels.total_charged_to_card")}</Text>
              <Text strong>{numberToCurrency(totalCharged)}</Text>
            </Flex>
          </>
        )}
        <Flex justify="space-between" align="center" className={styles.breakdownRow}>
          <Text>{t("bookings.cancel_modal.amount_paid")}</Text>
          <Text strong>{numberToCurrency(breakdownData.amount_paid_by_guest || 0)}</Text>
        </Flex>
        {breakdownData.amount_refunded > 0 && (
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Text>{t("bookings.cancel_modal.amount_refunded")}</Text>
            <Text strong>{numberToCurrency(breakdownData.amount_refunded)}</Text>
          </Flex>
        )}
      </Flex>
      <Flex vertical gap={8} className={styles.refundOptions}>
        <Radio.Group value={refundType} onChange={handleRadioChange}>
          <Flex gap={16}>
            <Radio value="full" disabled={isRefundDisabled}><Text strong>{t("bookings.cancel_modal.full_refund_label")}</Text></Radio>
            <Radio value="partial" disabled={isRefundDisabled}><Text strong>{t("bookings.cancel_modal.partial_refund_label")}</Text></Radio>
            <Radio value="no_refund"><Text strong>{t("bookings.cancel_modal.no_refund_label")}</Text></Radio>
          </Flex>
        </Radio.Group>
        {refundType === "full" && <Text type="secondary" className={styles.radioNote}>{t("bookings.cancel_modal.full_refund_note")}</Text>}
        {refundType === "partial" && (
          <Flex vertical gap={4}>
            <Input type="number" placeholder={t("bookings.cancel_modal.partial_amount_placeholder")} value={partialAmount} onChange={handlePartialAmountChange} prefix="$" min={0} status={partialAmountError ? "error" : ""} />
            {partialAmountError && <Text className={styles.errorText}>{partialAmountError}</Text>}
          </Flex>
        )}
        {refundType === "no_refund" && <Text type="secondary" className={styles.radioNote}>{t("bookings.cancel_modal.no_refund_note")}</Text>}
      </Flex>
    </>
  )
}

PaymentBreakdown.propTypes = {
  breakdownData: PropTypes.shape({
    tax_and_fee: PropTypes.number,
    custom_fees: PropTypes.array,
    total_price: PropTypes.number,
    amount_paid_by_guest: PropTypes.number,
    amount_refunded: PropTypes.number,
    refundable_amount: PropTypes.number
  }),
  breakdownLoading: PropTypes.bool,
  refundType: PropTypes.string.isRequired,
  partialAmount: PropTypes.string,
  partialAmountError: PropTypes.string,
  isRefundDisabled: PropTypes.bool,
  handleRadioChange: PropTypes.func.isRequired,
  handlePartialAmountChange: PropTypes.func.isRequired
}

export default PaymentBreakdown
